import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Grid from "@mui/material/Grid";
import FlagLoader from "layouts/flagloader";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import SalesTable from "examples/Tables/SalesTable";
import GradientLineChart from "examples/Charts/LineCharts/GradientLineChart";

import { API_URL } from "config";
import DefaultInfoCard from "./Components/DefaultInfoCard";
import Chart from "./Components/Chart";
import ProgressLineChart from "./Components/Progresschart/ProgressChart";
import MixedChart from "./Components/MixedChart";
import SalesTableDaily from "./Components/SalesTableDaily";
import { useAuth } from "../../../AuthContext";

const DASHBOARD_CARD_KEYS = {
    totalClients: ["dashboard.cards.total_clients.view"],
    monthlyRevenue: ["dashboard.cards.monthly_revenue.view"],
    revenueLastFiveDays: ["dashboard.cards.revenue_last_5_days.view"],
    usersFiveDays: ["dashboard.cards.users_5_days.view"],
    statusCount: ["dashboard.cards.status_count.view"],
    clientTotals: ["dashboard.cards.client_totals.view"],
    clientDailyTotals: ["dashboard.cards.client_daily_totals.view"],
    recentlyActiveProjects: ["dashboard.cards.recently_active_projects.view"],
};

const METRIC_KEYS = {
    completes: {
        full: ["dashboard.metrics.completes.full"],
        half: ["dashboard.metrics.completes.half"],
    },
    revenue: {
        full: ["dashboard.metrics.revenue.full"],
        half: ["dashboard.metrics.revenue.half"],
    },
};

const numberValue = (value) => Number(value || 0);

const formatNumber = (value) =>
    new Intl.NumberFormat(undefined, {
        maximumFractionDigits: 2,
    }).format(numberValue(value));

const formatCurrency = (value) =>
    `$${new Intl.NumberFormat(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(numberValue(value))}`;

function Default() {
    const navigate = useNavigate();
    const { permissions: authPermissions = [], isAuthReady } = useAuth();
    const hasPermission = (keys) => keys.some((key) => authPermissions.includes(key));
    const getMetricFactor = (metric) => {
        if (hasPermission(METRIC_KEYS[metric].full)) return 1;
        if (hasPermission(METRIC_KEYS[metric].half)) return 0.5;
        return null;
    };

    const completesFactor = getMetricFactor("completes");
    const revenueFactor = getMetricFactor("revenue");
    const canViewCompletesMetric = completesFactor !== null;
    const canViewRevenueMetric = revenueFactor !== null;
    const scaleMetric = (value, factor) =>
        factor === null ? null : numberValue(value) * factor;

    const canViewDashboard = hasPermission(["Dashboard", "viewDashboard", "dashboard"]);
    const canViewClients = canViewDashboard && hasPermission(DASHBOARD_CARD_KEYS.totalClients);
    const canViewMonthlyRevenue =
        canViewDashboard &&
        canViewRevenueMetric &&
        hasPermission(DASHBOARD_CARD_KEYS.monthlyRevenue);
    const canViewLastFiveDaysRevenue =
        canViewDashboard &&
        canViewRevenueMetric &&
        hasPermission(DASHBOARD_CARD_KEYS.revenueLastFiveDays);
    const canViewAttendance =
        canViewDashboard && hasPermission(DASHBOARD_CARD_KEYS.usersFiveDays);
    const canViewStatusCount =
        canViewDashboard && hasPermission(DASHBOARD_CARD_KEYS.statusCount);
    const canViewClientTotals =
        canViewDashboard &&
        (canViewCompletesMetric || canViewRevenueMetric) &&
        hasPermission(DASHBOARD_CARD_KEYS.clientTotals);
    const canViewClientDailyTotals =
        canViewDashboard &&
        (canViewCompletesMetric || canViewRevenueMetric) &&
        hasPermission(DASHBOARD_CARD_KEYS.clientDailyTotals);
    const canViewRecentlyActiveProjects =
        canViewDashboard &&
        hasPermission(DASHBOARD_CARD_KEYS.recentlyActiveProjects) &&
        hasPermission(["projects.pages.list.view"]) &&
        hasPermission(["projects.tabs.live.view"]) &&
        hasPermission(["projects.fields.status.view"]);

    const [statusLoading, setStatusLoading] = useState(true);
    const [monthLoading, setMonthLoading] = useState(true);
    const [revenueLoading, setRevenueLoading] = useState(true);
    const [userLoading, setUserLoading] = useState(true);
    const [clientLoading, setClientLoading] = useState(true);
    const [clientDailyLoading, setClientDailyLoading] = useState(true);
    const [recentlyActiveLoading, setRecentlyActiveLoading] = useState(true);

    const [totalClients, setTotalClients] = useState("");
    const [recentlyActiveProjects, setRecentlyActiveProjects] = useState(0);

    const [monthlyRevenue, setMonthlyRevenue] = useState({
        revenue: 0,
        change: "0%",
        color: "success",
    });

    const [monthlyRevenueChart, setMonthlyRevenueChart] = useState({
        labels: [],
        datasets: [],
    });


    const [dailyRevenue, setDailyRevenue] = useState({
        count: 0,
        chart: {
            labels: [],
            data: [],
        },
    });

    const [arrivalData, setArrivalData] = useState({
        labels: [],
        datasets: [
            {
                label: "Arrivals",
                color: "info",
                data: [],
            },
        ],
    });

    const [dailyRange, setDailyRange] = useState("24h");
    const [range, setRange] = useState("24h");

    const [statusChartData, setStatusChartData] = useState({
        labels: [],
        datasets: [],
    });

    const [clientRows, setClientRows] = useState([]);
    const [footer, setFooter] = useState(null);

    const [dailyClientRows, setDailyClientRows] = useState([]);
    const [dailyFooter, setDailyFooter] = useState(null);

    // =====================================================
    // DASHBOARD DATA APIs (UNCHANGED)
    // =====================================================

    useEffect(() => {
        if (!isAuthReady || !canViewClients) return;

        const fetchClients = async () => {
            try {
                const token = localStorage.getItem("authToken");

                const response = await fetch(`${API_URL}/clients/total-clients`, {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                });

                if (!response.ok) {
                    throw new Error("API failed");
                }

                const result = await response.json();
                if (result.success && result.data) {
                    setTotalClients(result.data.total_clients);
                } else {
                    setTotalClients(0);
                }
            } catch (error) {
                console.error("Error fetching total clients:", error);
                setTotalClients(0);
            }
        };

        fetchClients();
    }, [isAuthReady, canViewClients]);

    useEffect(() => {
        if (!isAuthReady || !canViewRecentlyActiveProjects) {
            setRecentlyActiveLoading(false);
            return undefined;
        }

        let isCurrent = true;

        const fetchRecentlyActiveProjects = async () => {
            try {
                setRecentlyActiveLoading(true);
                const token = localStorage.getItem("authToken");
                const response = await fetch(
                    `${API_URL}/projects?project_tab=live&recent_active=1&page=1&per_page=1`,
                    {
                        method: "GET",
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${token}`,
                        },
                    }
                );

                if (!response.ok) {
                    throw new Error("API failed");
                }

                const result = await response.json();
                if (isCurrent && result.success === 1) {
                    setRecentlyActiveProjects(Number(result.bids?.total || 0));
                }
            } catch (error) {
                console.error("Error fetching recently active projects:", error);
                if (isCurrent) setRecentlyActiveProjects(0);
            } finally {
                if (isCurrent) setRecentlyActiveLoading(false);
            }
        };

        fetchRecentlyActiveProjects();
        const intervalId = window.setInterval(fetchRecentlyActiveProjects, 60000);

        return () => {
            isCurrent = false;
            window.clearInterval(intervalId);
        };
    }, [isAuthReady, canViewRecentlyActiveProjects]);

    useEffect(() => {
        if (!isAuthReady || !canViewMonthlyRevenue) return;

        const fetchRevenueData = async () => {
            try {
                setMonthLoading(true);
                const token = localStorage.getItem("authToken");

                const response = await fetch(`${API_URL}/clients/total-monthwise-details`, {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                });

                if (!response.ok) {
                    throw new Error("API failed");
                }


                const result = await response.json();

                if (result.success) {
                    const revenueW0 = scaleMetric(result.totals.w0.total_revenue, revenueFactor) || 0;
                    const revenueW1 = scaleMetric(result.totals.w1.total_revenue, revenueFactor) || 0;
                    const completesW0 = scaleMetric(result.totals.w0.total_completes, completesFactor);
                    const completesW1 = scaleMetric(result.totals.w1.total_completes, completesFactor);
                    const datasets = [
                        {
                            label: "Revenue",
                            color: "info",
                            data: [revenueW0, revenueW1],
                        },
                    ];

                    if (completesW0 !== null && completesW1 !== null) {
                        datasets.push({
                            label: "Completes",
                            color: "dark",
                            data: [completesW0, completesW1],
                        });
                    }

                    setMonthlyRevenue({
                        revenue: revenueW1,
                        change: result.change_pct.revenue_str,
                        color: result.change_pct.revenue >= 0 ? "success" : "error",
                    });

                    setMonthlyRevenueChart({
                        labels: ["Previous Month", "Current Month"],
                        datasets,
                    });

                }
            } catch (error) {
                console.error("Error fetching monthly revenue:", error);
            } finally {
                setMonthLoading(false);
            }
        };

        fetchRevenueData();
    }, [isAuthReady, canViewMonthlyRevenue, revenueFactor, completesFactor]);

    useEffect(() => {
        if (!isAuthReady || !canViewLastFiveDaysRevenue) return;

        const fetchDailyRevenue = async () => {
            try {
                setRevenueLoading(true);
                const token = localStorage.getItem("authToken");

                const response = await fetch(`${API_URL}/clients/daily-totals/last-five`, {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                });

                if (!response.ok) {
                    throw new Error("API failed");
                }


                const result = await response.json();

                if (result.success && result.items) {
                    const labels = result.items.map((item) => item.date);
                    const data = result.items.map((item) => scaleMetric(item.revenue, revenueFactor) || 0);
                    const sumRevenue = scaleMetric(result.sum.revenue, revenueFactor) || 0;

                    setDailyRevenue({
                        count: formatCurrency(sumRevenue),
                        chart: { labels, data },
                        totals: result.sum,
                    });
                }
            } catch (error) {
                console.error("Error fetching daily revenue:", error);
            } finally {
                setRevenueLoading(false);
            }
        };

        fetchDailyRevenue();
    }, [isAuthReady, canViewLastFiveDaysRevenue, revenueFactor]);

    useEffect(() => {
        if (!isAuthReady || !canViewAttendance) return;

        const fetchArrivals = async () => {
            try {
                setUserLoading(true);
                const token = localStorage.getItem("authToken");

                const response = await fetch(`${API_URL}/attendance/arrivals/last-five`, {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                });

                if (!response.ok) {
                    throw new Error("API failed");
                }


                const result = await response.json();

                if (result && result.counts) {
                    const labels = result.counts.map((item) => item.date);
                    const data = result.counts.map((item) => item.arrived);

                    setArrivalData({
                        labels,
                        datasets: [
                            {
                                label: "Arrivals",
                                color: "info",
                                data,
                            },
                        ],
                    });
                } else {
                    setArrivalData({
                        labels: [],
                        datasets: [
                            {
                                label: "Arrivals",
                                color: "info",
                                data: [],
                            },
                        ],
                    });
                }
            } catch (error) {
                console.error("Error fetching arrivals:", error);
                setArrivalData({
                    labels: [],
                    datasets: [
                        {
                            label: "Arrivals",
                            color: "info",
                            data: [],
                        },
                    ],
                });
            } finally {
                setUserLoading(false);
            }
        };

        fetchArrivals();
    }, [isAuthReady, canViewAttendance]);

    useEffect(() => {
        if (!isAuthReady || !canViewStatusCount) return;

        const fetchStatusCounts = async () => {
            try {
                setStatusLoading(true);
                const token = localStorage.getItem("authToken");

                const response = await fetch(
                    `${API_URL}/studies/all-status-counts?range=${range}`,
                    {
                        method: "GET",
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${token}`,
                        },
                    }
                );

                if (!response.ok) {
                    throw new Error("API failed");
                }


                const result = await response.json();

                if (result.success && result.counts) {
                    const counts = result.counts;
                    const labels = ["Partial"];
                    const data = [counts.partial];

                    if (canViewCompletesMetric) {
                        labels.push("Completed");
                        data.push(scaleMetric(counts.completed, completesFactor) || 0);
                    }

                    labels.push(
                        "Terminate",
                        "Quota Full",
                        "S-Terminate"
                    );
                    data.push(
                        counts.terminate,
                        counts.quota_full,
                        counts.security_terminate
                    );

                    setStatusChartData({
                        labels,
                        datasets: [
                            {
                                label: `Status Count (${range})`,
                                color: "info",
                                data,
                                chartType: "bar",
                            },
                        ],
                    });
                } else {
                    setStatusChartData({ labels: [], datasets: [] });
                }
            } catch (error) {
                console.error("Error fetching status counts:", error);
                setStatusChartData({ labels: [], datasets: [] });
            } finally {
                setStatusLoading(false);
            }
        };

        fetchStatusCounts();
    }, [isAuthReady, canViewStatusCount, canViewCompletesMetric, completesFactor, range]);

    useEffect(() => {
        if (!isAuthReady || !canViewClientTotals) return;

        const fetchClientTotals = async () => {
            try {
                const token = localStorage.getItem("authToken");

                const response = await fetch(`${API_URL}/clients/total-details`, {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                });

                if (!response.ok) {
                    throw new Error("API failed");
                }


                const result = await response.json();

                if (result.success && result.data) {
                    const formattedRows = result.data.map((item) => {
                        const row = {
                            "Client Name": item.client_name,
                        };
                        const completes = scaleMetric(item.total_completes, completesFactor);
                        const revenue = scaleMetric(item.total_revenue, revenueFactor);

                        if (completes !== null) row.Completes = formatNumber(completes);
                        if (revenue !== null) row.Revenue = formatNumber(revenue);

                        row.Percentage = `${numberValue(item.percentage).toFixed(2)}%`;
                        return row;
                    });
                    const footerData = {
                        total_percentage: result.grand_totals?.total_percentage || 100,
                    };
                    const totalCompletes = scaleMetric(
                        result.grand_totals?.total_completes,
                        completesFactor
                    );
                    const totalRevenue = scaleMetric(
                        result.grand_totals?.total_revenue,
                        revenueFactor
                    );

                    if (totalCompletes !== null) footerData.total_completes = totalCompletes;
                    if (totalRevenue !== null) footerData.total_revenue = totalRevenue;

                    setClientRows(formattedRows);
                    setFooter(footerData);
                } else {
                    setClientRows([]);
                }
            } catch (error) {
                console.error("Error fetching client totals:", error);
                setClientRows([]);
            }

            finally {
                setClientLoading(false);
            }
        };

        fetchClientTotals();
    }, [isAuthReady, canViewClientTotals, completesFactor, revenueFactor]);


    useEffect(() => {
        if (!isAuthReady || !canViewClientDailyTotals) return;

        const fetchClientDailyTotals = async () => {
            try {

                setClientDailyLoading(true);

                const token = localStorage.getItem("authToken");

                const endpoint =
                    dailyRange === "7d"
                        ? "/clients/totalweekly-details"
                        : "/clients/totaldaily-details";


                const response = await fetch(`${API_URL}${endpoint}`, {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                });

                if (!response.ok) {
                    throw new Error("API failed");
                }


                const result = await response.json();

                if (result.success && result.data) {
                    const formattedRows = result.data.map((item) => {
                        const row = {
                            "Client Name": item.client_name,
                        };
                        const completes = scaleMetric(item.total_completes, completesFactor);
                        const revenue = scaleMetric(item.total_revenue, revenueFactor);

                        if (completes !== null) row.Completes = formatNumber(completes);
                        if (revenue !== null) row.Revenue = formatNumber(revenue);

                        row.Percentage = `${numberValue(item.percentage).toFixed(2)}%`;
                        return row;
                    });

                    setDailyClientRows(formattedRows);

                    const footerData = {
                        total_percentage: 100,
                    };
                    const totalCompletes = scaleMetric(
                        result.grand_totals?.total_completes,
                        completesFactor
                    );
                    const totalRevenue = scaleMetric(
                        result.grand_totals?.total_revenue,
                        revenueFactor
                    );

                    if (totalCompletes !== null) footerData.total_completes = totalCompletes;
                    if (totalRevenue !== null) footerData.total_revenue = totalRevenue;

                    setDailyFooter(footerData);
                } else {
                    setDailyClientRows([]);
                }
            } catch (error) {
                console.error("Error fetching client daily totals:", error);
                setDailyClientRows([]);
            }

            finally {
                setClientDailyLoading(false);
            }

        };

        fetchClientDailyTotals();
        // }, []);

    }, [isAuthReady, canViewClientDailyTotals, completesFactor, revenueFactor, dailyRange]);




    if (!isAuthReady) {
        return (
            <DashboardLayout>
                <DashboardNavbar />
                <SoftBox display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
                    <FlagLoader />
                </SoftBox>
                <Footer />
            </DashboardLayout>
        );
    }

    if (!canViewDashboard) {
        return (
            <DashboardLayout>
                <DashboardNavbar />
                <SoftBox display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
                    <SoftTypography variant="h6" color="text">
                        Dashboard access is not assigned.
                    </SoftTypography>
                </SoftBox>
                <Footer />
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout>
            <DashboardNavbar />

            <SoftBox px={{ xs: 2, md: 3 }} py={3}>
            <Grid container spacing={3} alignItems="stretch">
                {(canViewClients || canViewRecentlyActiveProjects) && (
                    <Grid item xs={12} sm={3} md={3} sx={{ display: "flex" }}>
                        <SoftBox display="flex" flexDirection="column" gap={1.5} width="100%" height="100%">
                            {canViewClients && (
                                <SoftBox sx={{ flex: 1 }}>
                                    <DefaultInfoCard
                                        icon="person_icon"
                                        title="Totals Clients"
                                        value={totalClients !== null ? totalClients.toString() : "Loading..."}
                                        compact={canViewRecentlyActiveProjects}
                                        minHeight={canViewRecentlyActiveProjects ? 104 : 220}
                                    />
                                </SoftBox>
                            )}
                            {canViewRecentlyActiveProjects && (
                                <SoftBox sx={{ flex: 1 }}>
                                    <DefaultInfoCard
                                        color="success"
                                        icon="schedule"
                                        title="Recently Active"
                                        description="(Last one hour)"
                                        value={recentlyActiveLoading ? "Loading..." : recentlyActiveProjects.toString()}
                                        compact={canViewClients}
                                        minHeight={canViewClients ? 104 : 220}
                                        onClick={() => navigate("/projects/list?tab=live&recent_active=1")}
                                    />
                                </SoftBox>
                            )}
                        </SoftBox>
                    </Grid>
                )}

                {canViewMonthlyRevenue && (
                    <Grid item xs={12} sm={4} md={4}>

                        {monthLoading ? (
                            <SoftBox
                                display="flex"
                                justifyContent="center"
                                alignItems="center"
                                height="100%"
                                minHeight="300px"
                            >
                                <FlagLoader />
                            </SoftBox>
                        ) : (
                            <Chart
                                title="Monthly Revenue"
                                height="375px"
                                count={formatCurrency(monthlyRevenue.revenue)}
                                percentage={{ color: monthlyRevenue.color, label: monthlyRevenue.change }}
                                chart={monthlyRevenueChart}
                            />

                        )}
                    </Grid>
                )}

                {canViewLastFiveDaysRevenue && (
                    <Grid item xs={12} sm={5} md={5}>

                        {revenueLoading ? (
                            <SoftBox
                                display="flex"
                                justifyContent="center"
                                alignItems="center"
                                height="100%"
                                minHeight="300px"
                            >
                                <FlagLoader />
                            </SoftBox>
                        ) : (
                            <ProgressLineChart
                                height="275px"
                                icon="date_range"
                                title="Revenue of 5 days"
                                count={dailyRevenue.count}
                                progress={100}
                                chart={dailyRevenue.chart}
                            />
                        )}
                    </Grid>
                )}
            </Grid>

            <Grid container spacing={3} mt={3} alignItems="stretch">
                {canViewAttendance && (
                    <Grid item xs={12} sm={6} md={6}>


                        {userLoading ? (
                            <SoftBox
                                display="flex"
                                justifyContent="center"
                                alignItems="center"
                                height="100%"
                                minHeight="300px"
                            >
                                <FlagLoader />
                            </SoftBox>
                        ) : (
                            <GradientLineChart
                                height="300px"
                                title="Users 5 Days Count"
                                chart={arrivalData}
                                compact
                            />
                        )}
                    </Grid>
                )}

                {canViewStatusCount && (
                    <Grid item xs={12} sm={6} md={6}>

                        {statusLoading ? (
                            <SoftBox
                                display="flex"
                                justifyContent="center"
                                alignItems="center"
                                height="100%"
                                minHeight="300px"
                            >
                                <FlagLoader />
                            </SoftBox>
                        ) : (
                            <MixedChart
                                title="Status Count"
                                height="300px"
                                chart={statusChartData}
                                range={range}
                                setRange={setRange}
                            />
                        )}
                    </Grid>
                )}
            </Grid>

            <Grid container spacing={3} mt={3} alignItems="stretch">
                {canViewClientTotals && (
                    <Grid item xs={12} sm={6} md={6}>

                        {clientLoading ? (
                            <SoftBox
                                display="flex"
                                justifyContent="center"
                                alignItems="center"
                                height="100%"
                                minHeight="300px"
                            >
                                <FlagLoader />
                            </SoftBox>
                        ) : (
                            <SalesTable
                                title="Client's Complete Count"
                                rows={clientRows}
                                footerData={footer}
                                compact
                            />
                        )}
                    </Grid>
                )}

                {canViewClientDailyTotals && (
                    <Grid item xs={12} sm={6} md={6}>

                        {clientDailyLoading ? (
                            <SoftBox
                                display="flex"
                                justifyContent="center"
                                alignItems="center"
                                height="100%"
                                minHeight="300px"
                            >
                                <FlagLoader />
                            </SoftBox>
                        ) : (
                            <SalesTableDaily
                                title="Client's Complete Count Daily"
                                range={dailyRange}
                                rows={dailyClientRows}
                                footerData={dailyFooter}
                                onRangeChange={setDailyRange}
                            />
                        )}
                    </Grid>
                )}
            </Grid>

            </SoftBox>
            <Footer />
        </DashboardLayout>
    );
}

export default Default;
